/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  cloneElement, forwardRef,
  isValidElement, ReactElement, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import animate, { easeInOut } from '../animate';

type FadeProps = {
  isVisible?: boolean;
  duration?: number;
  timing?: (t: number) => number; // strictly one element -
  // HTML tag or React Component that returns only one html element
  children: ReactElement<unknown>; // один ReactElement
  mountOnEnter?: boolean
  unmountOnExit?: boolean
  onExit?: () => void
  onExited?: () => void
  onEnter?: () => void
  onEntered?: () => void
};

const Fade = forwardRef<HTMLDivElement, FadeProps>(({
  isVisible,
  duration = 300,
  timing = easeInOut,
  children,
  mountOnEnter,
  unmountOnExit,
  onExit,
  onExited,
  onEnter,
  onEntered,
}, ref) => {
  const [opacity, setOpacity] = useState<number | ''>(isVisible ? 1 : 0);
  const [mounted, setMounted] = useState<boolean>(
    mountOnEnter ? !!isVisible : true,
  );
  const firstRender = useRef(true);
  const runId = useRef(0);

  // проверка: один элемент и валидный
  if (!isValidElement(children)) {
    throw new Error('Fade awaits only ReactElement as children that not Fragment');
  }

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    // animate() нельзя остановить, поэтому досрочно устаревшие анимации
    // отсекаются по токену, иначе end() прошлого закрытия размонтирует видимый элемент
    runId.current += 1;
    const currentRun = runId.current;
    const isStale = () => runId.current !== currentRun;

    if (isVisible) {
      if (mountOnEnter) {
        setMounted(true);
        setOpacity(0);
      }
      animate({
        duration,
        timing,
        draw: (progress) => {
          if (isStale()) return;
          setOpacity(progress);
        },
        end: () => {
          if (isStale()) return;
          if (onEntered) {
            onEntered();
          }
        },
      });
    } else {
      if (onExit) {
        onExit();
      }
      animate({
        duration,
        timing,
        draw: (progress) => {
          if (isStale()) return;
          setOpacity(1 - progress);
        },
        end: () => {
          if (isStale()) return;
          if (onExited) {
            onExited();
          }
          if (unmountOnExit) {
            setMounted(false);
          }
        },
      });
    }
  }, [isVisible]);

  useLayoutEffect(() => {
    if (mounted) {
      onEnter?.();
    }
  }, [mounted]);

  if (!mounted) return null;

  return cloneElement(children, {
    ref,
    style: {
      ...(children.props as any).style,
      opacity: opacity === 1 ? undefined : opacity,
      visibility: opacity === 0 ? 'hidden' : undefined,
    },
  } as any);
});

export default Fade;
